import classNames from 'classnames';
import Immutable from 'immutable';
import React from 'react';
import { useSelector } from 'react-redux';
import ArtboardCell from 'components/ArtboardCell';
import Column from 'components/ArtboardLayout/components/Column';
import RowResizer from 'components/RowResizer';
import * as Models from 'models';
import { getMinColumnWidth } from 'utils/relations/getMinColumnWidth';
import { isRegularRelation } from 'utils/relations/isRegularRelation';
import { rowSelector } from './selectors';
import styles from './styles.module.scss';

type Props = {
  activeLayoutIds: Immutable.Set<string>;
  columnRelationId: string;
  disableCellWidthEditing?: boolean;
  editMode: boolean;
  height: number;
  isLast: boolean;
  isLayoutEmpty: boolean;
  isOpenToolbar: boolean;
  isReusableLayout: boolean;
  isSectionActive: boolean;
  layout: Models.LayoutMap;
  layoutId: string;
  layoutRelations: Models.LayeredRelationsMap;
  level: number;
  positionWithinColumn: number;
  relation: Models.RowRelationMap;
  sectionStyles: Models.MasterScreen.SectionStylesMap;
  setRowHeightSilently(relationId: string, position: number, height: number): void;
  showRowResizer?: boolean;
  ssi: Models.SSIMap;
  toggleExpandedMode(isExpanded: boolean): void;
  toggleLayoutRelations(relations: Models.LayeredRelationsMap): void;
  dataTestId: string;
};

const Row: React.FunctionComponent<Props> = (props) => {
  const {
    activeLayoutIds,
    columnRelationId: parentColumnRelationId,
    disableCellWidthEditing = false,
    editMode,
    height,
    isLast,
    isLayoutEmpty,
    isOpenToolbar,
    isReusableLayout,
    isSectionActive,
    layout,
    layoutId,
    layoutRelations,
    level,
    positionWithinColumn,
    relation,
    sectionStyles,
    setRowHeightSilently,
    showRowResizer = false,
    ssi,
    toggleExpandedMode,
    toggleLayoutRelations,
    dataTestId,
  } = props;
  const rowRelationId = relation.get('id');
  const relationIds = relation.get('relationIds');
  const columnsWidth = relation.getIn(['styles', 'columnsWidth']);
  const container = React.useRef<HTMLDivElement>();
  const hasHeightBeenDispatched = React.useRef<boolean>(false);
  const { isResizingRow, isReadOnly, activeLayer } = useSelector(rowSelector);

  React.useEffect(
    () => {
      if (height || !container.current) {
        return;
      }
      const { clientHeight: newHeight } = container.current;

      if (newHeight === height) {
        hasHeightBeenDispatched.current = false;

        return;
      }

      if (hasHeightBeenDispatched.current) {
        return;
      }

      setRowHeightSilently(parentColumnRelationId, positionWithinColumn, newHeight);
      hasHeightBeenDispatched.current = true;
    },
    [],
  );
  const rowTestIndex = `${dataTestId}-row${positionWithinColumn}`;

  return (
    <div
      className={classNames(styles.Row, {
        [styles.stretchable]: isLast && !height,
      })}
      ref={container}
      data-testid={rowTestIndex}
    >
      {
        // initially sliders are shown only within active section,
        // but when user resizes row on border of two section and move away from active one we should not hide slider
        (isSectionActive || isResizingRow) && showRowResizer && !isReadOnly &&
        <RowResizer
          container={container}
          height={height}
          layoutId={layoutId}
          relationIsBeingResizedId={relation.get('id')}
          relations={layoutRelations}
          toggleRelations={toggleLayoutRelations}
        />
      }
      {relationIds.map((columnRelationId, columnPosition) => {
        const currentRelation = layoutRelations.get(columnRelationId) as Models.ColumnRelationMap | Models.LayeredRegularRelationMap;
        const isNotLastColumn = columnPosition !== relationIds.size - 1;
        const showColumnResizer = isNotLastColumn && !isOpenToolbar;
        const columnWidth = columnsWidth.get(columnPosition);
        const columnsCount = relationIds.size;

        if (isRegularRelation(currentRelation)) {
          // All cells are changed along with the next one except the last one. The last cell changes along with the previous one.
          const isIncreasingWidthDisabled = isNotLastColumn
            ? getMinColumnWidth(relationIds.get(columnPosition + 1), layoutRelations) >= columnsWidth.get(columnPosition + 1)
            : getMinColumnWidth(relationIds.get(columnPosition - 1), layoutRelations) >= columnsWidth.get(columnPosition - 1);

          return (
            <ArtboardCell
              cellPosition={0}
              dataTestId={rowTestIndex}
              index={columnPosition ?? 0}
              cellsCount={1}
              columnRelationId={parentColumnRelationId}
              columnsCount={columnsCount}
              disableCellWidthEditing={disableCellWidthEditing}
              height={height}
              isIncreasingWidthDisabled={isIncreasingWidthDisabled}
              isLayoutEmpty={isLayoutEmpty}
              isReusableLayout={isReusableLayout}
              isSectionActive={isSectionActive}
              key={columnRelationId}
              layout={layout}
              layoutId={layoutId}
              layoutRelations={layoutRelations}
              notEditable={!!ssi || isReadOnly}
              parentRelationId={rowRelationId}
              positionWithinColumn={positionWithinColumn}
              relation={currentRelation}
              sectionStyles={sectionStyles}
              setRowHeightSilently={setRowHeightSilently}
              showColumnResizer={showColumnResizer}
              showRowResizer={false}
              toggleExpandedMode={toggleExpandedMode}
              toggleLayoutRelations={toggleLayoutRelations}
              width={columnWidth}
              activeLayer={activeLayer}
            />
          );
        }

        return (
          <Column
            activeLayoutIds={activeLayoutIds}
            columnsCount={columnsCount}
            disableCellWidthEditing={disableCellWidthEditing}
            editMode={editMode}
            height={height}
            isLayoutEmpty={isLayoutEmpty}
            isOpenToolbar={isOpenToolbar}
            isReusableLayout={isReusableLayout}
            isSectionActive={isSectionActive}
            key={columnRelationId}
            layout={layout}
            layoutId={layoutId}
            layoutRelations={layoutRelations}
            level={level + 1}
            relation={currentRelation}
            sectionStyles={sectionStyles}
            setRowHeightSilently={setRowHeightSilently}
            showColumnResizer={showColumnResizer}
            ssi={ssi}
            toggleExpandedMode={toggleExpandedMode}
            toggleLayoutRelations={toggleLayoutRelations}
            width={columnWidth}
            dataTestId={`${rowTestIndex}-column${columnPosition}`}
          />
        );
      })}
    </div>
  );
};

export default Row;
